// doctorAuth.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import { API_URL, AppConstant } from '../../../../AppConstant'
import { APIStatus } from '../../../types'

export interface Doctor {
  id: string
  clinicId: string
  isActive: boolean
  createdAt: string // ISO Date string
  updatedAt: string
  user: {
    id: string
    firstname: string
    lastname: string
    phone: string
    unverifiedPhone: string
    dob: string | null
    email: string
    unverifiedEmail: string
    signupThrough: string
    referralCode: string | null
    externalUserId: string | null
    emailVerificationId: string | null
    welcomeEmailSend: boolean
    textConsent: boolean
    profilePic: string
    screeningStatus: string
    gender: string | null
    isDeleted: boolean
    createdAt: string
    updatedAt: string
  }
}

interface DoctorAuthState {
  // Form state
  selectedProfile: Doctor | null
  otp: string

  // Auth state
  doctor: Doctor | null

  // OTP async
  sendDoctorOtpStatus: APIStatus
  sendDoctorOtpError: string | null

  verifyDoctorOtpStatus: APIStatus
  verifyDoctorOtpError: string | null

  // Doctor list async
  doctorList: Doctor[] | null
  doctorListStatus: APIStatus
  doctorListError: string | null
}

const initialState: DoctorAuthState = {
  selectedProfile: null,
  otp: '',

  doctor: null,

  sendDoctorOtpStatus: APIStatus.idle,
  sendDoctorOtpError: null,

  verifyDoctorOtpStatus: APIStatus.idle,
  verifyDoctorOtpError: null,

  doctorList: null,
  doctorListStatus: APIStatus.idle,
  doctorListError: null,
}

interface SendPayload {
  id: string
}
interface VerifyPayload {
  id: string
  code: string
}

// SEND OTP
export const sendDoctorOtpAsync = createAsyncThunk<{ id: string }, SendPayload, { rejectValue: string }>(
  'doctorAuth/sendOtp',
  async ({ id }, thunkAPI) => {
    try {
      const res = await axios.post(`${API_URL}/auth/chiropractor/login`, { id })
      if (res && res.data.code === 'success') {
        return thunkAPI.fulfillWithValue(res.data)
      } else {
        return thunkAPI.rejectWithValue(res.data.message)
      }
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Failed to send OTP')
    }
  },
)

// VERIFY OTP
export const verifyDoctorOtpAsync = createAsyncThunk<Doctor, { id: string; code: string }, { rejectValue: string }>(
  'doctorAuth/verifyOtp',
  async ({ id, code }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/chiropractor/login/verify`,
        { id, code },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(AppConstant.ADMIN_TOKEN)}`,
            'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
          },
        },
      )
      if (response && response.data.code === 'success' && response.data.data) {
        localStorage.setItem(AppConstant.DOCTOR_TOKEN, response.data.data.accessToken)

        return thunkAPI.fulfillWithValue(response.data.data)
      } else {
        return thunkAPI.rejectWithValue(response?.data?.message || 'Failed to verify OTP')
      }
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Verification failed')
    }
  },
)

// GET DOCTOR LIST
export const getDoctorListAsync = createAsyncThunk<Doctor[], void, { rejectValue: string }>(
  'doctorAuth/getDoctorList',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(`${API_URL}/clinic/chiropractor/public`, {
        headers: {
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      })
      if (response && response.data.code === 'success' && response.data.data) {
        return thunkAPI.fulfillWithValue(response.data.data)
      } else {
        return thunkAPI.rejectWithValue(response?.data?.message || 'Failed to load doctor list')
      }
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Failed to load doctor list')
    }
  },
)

const doctorAuthSlice = createSlice({
  name: 'doctorAuth',
  initialState,
  reducers: {
    // Form reducers
    setDoctorSelectedProfile(state, action: PayloadAction<Doctor | null>) {
      state.selectedProfile = action.payload
    },
    setDoctorOtp(state, action: PayloadAction<string>) {
      state.otp = action.payload
    },
    resetDoctorForm() {
      return { ...initialState }
    },
    resetDoctorListStatus(state) {
      state.doctorListStatus = APIStatus.idle
      state.doctorListError = null
    },

    // Auth reducers
    clearDoctorAuth(state) {
      state.doctor = null
      state.sendDoctorOtpStatus = APIStatus.idle
      state.sendDoctorOtpError = null
      state.verifyDoctorOtpStatus = APIStatus.idle
      state.verifyDoctorOtpError = null
      localStorage.removeItem(AppConstant.DOCTOR_TOKEN)
    },
  },

  extraReducers: (builder) => {
    builder
      // SEND OTP
      .addCase(sendDoctorOtpAsync.pending, (state) => {
        state.sendDoctorOtpStatus = APIStatus.pending
        state.sendDoctorOtpError = null
      })
      .addCase(sendDoctorOtpAsync.fulfilled, (state) => {
        state.sendDoctorOtpStatus = APIStatus.fulfilled
      })
      .addCase(sendDoctorOtpAsync.rejected, (state, action) => {
        state.sendDoctorOtpStatus = APIStatus.rejected
        state.sendDoctorOtpError = action.payload || 'Failed to send OTP'
      })

      // VERIFY OTP
      .addCase(verifyDoctorOtpAsync.pending, (state) => {
        state.verifyDoctorOtpStatus = APIStatus.pending
        state.verifyDoctorOtpError = null
      })
      .addCase(verifyDoctorOtpAsync.fulfilled, (state, action) => {
        state.verifyDoctorOtpStatus = APIStatus.fulfilled
        state.doctor = state.selectedProfile
      })
      .addCase(verifyDoctorOtpAsync.rejected, (state, action) => {
        state.verifyDoctorOtpStatus = APIStatus.rejected
        state.verifyDoctorOtpError = action.payload || 'Verification failed'
      })

      // GET DOCTOR LIST
      .addCase(getDoctorListAsync.pending, (state) => {
        state.doctorListStatus = APIStatus.pending
        state.doctorListError = null
      })
      .addCase(getDoctorListAsync.fulfilled, (state, action) => {
        state.doctorListStatus = APIStatus.fulfilled
        state.doctorList = action.payload
      })
      .addCase(getDoctorListAsync.rejected, (state, action) => {
        state.doctorListStatus = APIStatus.rejected
        state.doctorListError = action.payload || 'Failed to load doctor list'
      })
  },
})

export const { setDoctorSelectedProfile, setDoctorOtp, resetDoctorForm, clearDoctorAuth, resetDoctorListStatus } =
  doctorAuthSlice.actions

export default doctorAuthSlice.reducer
